import { z } from "zod";
import { graderSchema } from "./eval.js";
import { testReplySchema, testToolCallTraceSchema } from "./testConsole.js";

/**
 * A prompt revision proposed for one piece of feedback, with the evidence
 * behind it (PLAN.md "Agency admin: test before publishing").
 *
 * The evidence is the point. A model asked to rewrite a prompt from a
 * complaint alone will happily invent an instruction for a failure whose
 * real cause was an empty catalogue, a disabled tool or a bad search query —
 * and an admin has no way to tell those apart from the revision text. So a
 * suggestion carries what the agent looked at (`investigation`) and what
 * actually changed when the failed turn was replayed under the candidate
 * prompt (`baseline` vs `verification`).
 */
export const promptSuggestionStatusSchema = z.enum(["running", "ready", "failed", "applied", "rejected"]);
export type PromptSuggestionStatus = z.infer<typeof promptSuggestionStatusSchema>;

/** One step of the agent's investigation — the same shape the test console's trace pane already renders. */
export const suggestionToolCallSchema = testToolCallTraceSchema;
export type SuggestionToolCall = z.infer<typeof suggestionToolCallSchema>;

/**
 * One replay of the shopper's own question under a candidate prompt. This
 * is the answer to "would this change actually help?", and it is the reason
 * the agent has a `try_prompt` tool rather than only a rewrite instruction.
 */
export const promptVerificationSchema = z.object({
  prompt: z.string(),
  message: z.string(),
  reply: testReplySchema.nullable(),
  toolCalls: z.array(testToolCallTraceSchema).default([]),
  outcome: z.enum(["completed", "blocked", "aborted", "error"]),
});
export type PromptVerification = z.infer<typeof promptVerificationSchema>;

export const promptSuggestionDtoSchema = z.object({
  id: z.string().uuid(),
  feedbackId: z.string().uuid(),
  botId: z.string().uuid(),
  conversationId: z.string().uuid(),
  status: promptSuggestionStatusSchema,
  /** The draft prompt as it stood when the agent ran — the left side of the diff, frozen so a draft edited since can be detected rather than silently reverted. */
  basePrompt: z.string(),
  suggestedPrompt: z.string().nullable(),
  rationale: z.string().nullable(),
  /** False is a legitimate, useful answer: the feedback implied no durable instruction. */
  changed: z.boolean(),
  noChangeReason: z.string().nullable(),
  verificationSummary: z.string().nullable(),
  /** The reply the shopper's question gets under the *current* prompt, run before the agent starts, so before/after is observed rather than described. */
  baseline: promptVerificationSchema.nullable(),
  verification: z.array(promptVerificationSchema).default([]),
  investigation: z.array(suggestionToolCallSchema).default([]),
  proposedGraders: z.array(graderSchema).default([]),
  proposedCaseName: z.string().nullable(),
  /** Set once an admin has turned this feedback into a permanent check. */
  evalCaseId: z.string().uuid().nullable(),
  evalSuiteId: z.string().uuid().nullable(),
  error: z.string().nullable(),
  model: z.string().nullable(),
  costUsd: z.string().nullable(),
  createdAt: z.string(),
  decidedAt: z.string().nullable(),
});
export type PromptSuggestionDto = z.infer<typeof promptSuggestionDtoSchema>;

/** What `apply` sends back when the draft moved underneath the suggestion — a result the panel renders, not an error it throws. */
export const promptSuggestionStaleResponseSchema = z.object({
  error: z.literal("draft_moved"),
  currentPrompt: z.string(),
});
export type PromptSuggestionStaleResponse = z.infer<typeof promptSuggestionStaleResponseSchema>;

export const attachEvalCaseBodySchema = z.object({
  suiteId: z.string().uuid(),
  caseId: z.string().uuid(),
});
export type AttachEvalCaseBody = z.infer<typeof attachEvalCaseBodySchema>;
